import { supabase } from '../lib/supabase';

export class AchievementService {
  async checkAndUnlockAchievements(userId: string): Promise<void> {
    try {
      const checks = [
        this.checkRecordingAchievements(userId),
        this.checkScoreAchievements(userId),
        this.checkTournamentAchievements(userId)
      ];

      await Promise.all(checks);
    } catch (error) {
      console.error('Error checking achievements:', error);
    }
  }

  private async checkRecordingAchievements(userId: string): Promise<void> {
    const { count } = await supabase
      .from('recordings')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId);

    if (!count) return;

    const milestones = [
      { count: 1, title: 'First Steps' },
      { count: 5, title: 'Getting Started' },
      { count: 25, title: 'Rising Star' },
      { count: 50, title: 'Performer' },
      { count: 100, title: 'Vocal Legend' },
      { count: 250, title: 'Recording Marathon' }
    ];

    for (const milestone of milestones) {
      if (count >= milestone.count) {
        await this.unlockAchievementByTitle(userId, milestone.title);
      }
    }
  }

  private async checkScoreAchievements(userId: string): Promise<void> {
    try {
      const { data: performances } = await supabase
        .from('performances')
        .select(`
          id,
          performance_analysis(overall_score)
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(100);

      if (!performances || performances.length === 0) return;

      let highestScore = 0;
      for (const perf of performances) {
        const analysis = perf.performance_analysis as any;
        if (analysis && Array.isArray(analysis) && analysis.length > 0) {
          const score = analysis[0].overall_score;
          if (score > highestScore) {
            highestScore = score;
          }
        }
      }

      if (highestScore === 0) return;

      const scoreThresholds = [
        { score: 80, title: 'Natural Talent' },
        { score: 90, title: 'High Achiever' },
        { score: 95, title: 'Perfection Seeker' },
        { score: 98, title: 'Flawless Victory' }
      ];

      for (const threshold of scoreThresholds) {
        if (highestScore >= threshold.score) {
          await this.unlockAchievementByTitle(userId, threshold.title);
        }
      }
    } catch (error) {
      console.error('Error checking score achievements:', error);
    }
  }

  private async checkTournamentAchievements(userId: string): Promise<void> {
    const { count: participationCount } = await supabase
      .from('tournament_entries')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId);

    if (participationCount) {
      const milestones = [
        { count: 1, title: 'Tournament Rookie' },
        { count: 5, title: 'Competitive Spirit' },
        { count: 15, title: 'Tournament Regular' },
        { count: 50, title: 'Tournament Veteran' }
      ];

      for (const milestone of milestones) {
        if (participationCount >= milestone.count) {
          await this.unlockAchievementByTitle(userId, milestone.title);
        }
      }
    }
  }

  private async unlockAchievementByTitle(userId: string, title: string): Promise<void> {
    try {
      const { data: achievement } = await supabase
        .from('achievements')
        .select('id')
        .eq('title', title)
        .is('deleted_at', null)
        .maybeSingle();

      if (!achievement) return;

      const { data: existing } = await supabase
        .from('user_achievements')
        .select('id')
        .eq('user_id', userId)
        .eq('achievement_id', achievement.id)
        .maybeSingle();

      if (existing) return;

      await supabase.from('user_achievements').insert({
        user_id: userId,
        achievement_id: achievement.id,
        progress: 100,
        unlocked_at: new Date().toISOString()
      });

      console.log(`Achievement unlocked: ${title}`);
    } catch (error) {
      console.error(`Error unlocking achievement ${title}:`, error);
    }
  }

  async getUserAchievements(userId: string) {
    try {
      const { data: userAchievements, error } = await supabase
        .from('user_achievements')
        .select(`
          *,
          achievement:achievements(*)
        `)
        .eq('user_id', userId);

      if (error) throw error;

      return userAchievements || [];
    } catch (error) {
      console.error('Error getting user achievements:', error);
      return [];
    }
  }

  async getRecentUnlocks(userId: string, limit: number = 5) {
    try {
      const { data, error } = await supabase
        .from('user_achievements')
        .select(`
          *,
          achievement:achievements(*)
        `)
        .eq('user_id', userId)
        .order('unlocked_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error('Error getting recent unlocks:', error);
      return [];
    }
  }
}

export const achievementService = new AchievementService();
